import type { CategoryGroup } from "@/lib/finance";

export type CategoryStatus = "cubierto" | "en_ritmo" | "va_adelantada" | "casi_al_tope" | "se_paso";
export type PlanGroup = "essentials" | "comfort" | "debt" | "savings_investment";
export type ExpenseType = "essentials" | "comfort" | "debt";
export type TypeTag = "E" | "C" | "D";
export const TYPE_OPTS: { value: ExpenseType; label: string; tag: TypeTag }[] = [
  { value: "essentials", label: "Esencial", tag: "E" }, { value: "comfort", label: "Comodidad", tag: "C" }, { value: "debt", label: "Deuda", tag: "D" },
];
export const isExpenseType = (g?: string | null): g is ExpenseType => TYPE_OPTS.some((t) => t.value === g);
export const typeTag = (g?: string | null): TypeTag | null => TYPE_OPTS.find((t) => t.value === g)?.tag ?? null;
export const typeLabel = (g?: string | null) => TYPE_OPTS.find((t) => t.value === g)?.label ?? "";
export const TAG_STYLES: Record<TypeTag, string> = { E: "bg-[#DFE7F3] text-[#16294D]", C: "bg-[#ECE3D3] text-[#1A1F2B]", D: "bg-[#F6E3DC] text-[#9C3D24]" };
const TYPE_HINTS: [string, ExpenseType][] = [
  ["hipoteca", "debt"], ["crédito", "debt"], ["credito", "debt"], ["préstamo", "debt"], ["prestamo", "debt"], ["mensualidad", "debt"],
  ["compras a meses", "debt"], ["intereses", "debt"], ["comisiones", "debt"], ["anualidad", "debt"], ["impuestos", "essentials"],
  ["seguro", "essentials"], ["netflix", "comfort"], ["spotify", "comfort"], ["streaming", "comfort"], ["luz", "essentials"], ["agua", "essentials"], ["gas", "essentials"],
];
export function typeFor(name: string, cat?: { name: string; group: string }): ExpenseType {
  const n = name.trim().toLowerCase();
  const hit = TYPE_HINTS.find(([k]) => n.startsWith(k));
  if (hit) return hit[1];
  if (cat && isDebtName(cat.name)) return "debt";
  return cat?.group === "comfort" ? "comfort" : "essentials";
}

export interface CategoryMonth {
  id: string;
  name: string;
  type: "income" | "expense" | "transfer";
  group: CategoryGroup;
  is_archived: boolean;
  monthly_limit: number | null;
  description?: string | null;
  adjustment: number;
  effective_limit: number;
  actual_month_amount: number;
  remaining: number;
  progress_ratio: number | null;
  month_progress_ratio: number;
  status: CategoryStatus;
  helper_text: string;
}

export interface BudgetAlert {
  id: string;
  alert_type: string;
  title: string;
  body: string;
  created_at: string;
}

export interface BudgetGroup {
  group: PlanGroup;
  planned: number;
  actual: number;
  remaining: number;
  progress_ratio: number | null;
  categories: CategoryMonth[];
}

export interface BudgetResponse {
  month: string;
  income_base: number;
  planned: Record<PlanGroup | "total" | "unassigned", number>;
  actual: Record<PlanGroup | "total", number>;
  income_actual: number;
  remaining_to_spend: number;
  is_overplanned: boolean;
  month_progress_ratio: number;
  groups: BudgetGroup[];
  income_categories: CategoryMonth[];
  items: BudgetItemRow[];
  alerts: BudgetAlert[];
  msi_lines: MsiLine[];
  savings_flow: { contributed: number; withdrawn: number; net: number };
}

export interface MsiLine { category_id: string; budget_item_id: string | null; amount: number; index: number; total: number; label: string }

export const STATUS_LABELS: Record<CategoryStatus, string> = {
  cubierto: "Cubierto",
  en_ritmo: "En ritmo",
  va_adelantada: "Va adelantada",
  casi_al_tope: "Casi al tope",
  se_paso: "Se pasó",
};

export const STATUS_STYLES: Record<CategoryStatus, { chip: string; bar: string }> = {
  cubierto: { chip: "bg-[#E8F3EE] text-[#1B6E4F]", bar: "bg-[#1B6E4F]" },
  en_ritmo: { chip: "bg-[#DFE7F3] text-[#16294D]", bar: "bg-[#16294D]" },
  va_adelantada: { chip: "bg-[#F6EEDC] text-[#6E4810]", bar: "bg-[#B7862A]" },
  casi_al_tope: { chip: "bg-[#F1E3C2] text-[#553A0E]", bar: "bg-[#9A5B13]" },
  se_paso: { chip: "bg-[#F8ECE8] text-[#B23A1E]", bar: "bg-[#B23A1E]" },
};

export const GROUP_COLORS: Record<PlanGroup | "unassigned", string> = {
  essentials: "bg-[#16294D]",
  comfort: "bg-[#9DB2D6]",
  debt: "bg-[#9C3D24]",
  savings_investment: "bg-[#2C4F91]",
  unassigned: "bg-[#E7DECF]",
};

export const GROUP_HINTS: Record<PlanGroup, string> = {
  essentials: "Lo que necesitas para vivir: casa, servicios, comida, transporte, salud, educación y protección.",
  comfort: "Lo que hace la vida más agradable: ropa, salidas, viajes, cuidado personal y regalos.",
  debt: "Lo que pagas por deudas: préstamos, meses sin intereses, intereses, comisiones y anualidades.",
  savings_investment: "Lo que apartas para tu futuro. Es un traspaso a ahorro o inversión, no un gasto.",
};

export type Section = "income" | "expense" | "savings";
export type Frequency = "semimonthly" | "monthly" | "bimonthly" | "quarterly" | "semiannual" | "annual";
export const FREQUENCIES: { value: Frequency; label: string; period: number }[] = [
  { value: "semimonthly", label: "Quincenal", period: 0.5 }, { value: "monthly", label: "Mensual", period: 1 }, { value: "bimonthly", label: "Bimestral", period: 2 },
  { value: "quarterly", label: "Trimestral", period: 3 }, { value: "semiannual", label: "Semestral", period: 6 },
  { value: "annual", label: "Anual", period: 12 },
];
export const periodOf = (f: Frequency) => FREQUENCIES.find((x) => x.value === f)?.period ?? 1;
const idx = (ym: string) => { const [y, m] = ym.split("-").map(Number); return y * 12 + m; };
export const paysIn = (f: Frequency, payMonth: string | null | undefined, month: string) =>
  periodOf(f) <= 1 || !payMonth || (((idx(month) - idx(payMonth)) % periodOf(f)) + periodOf(f)) % periodOf(f) === 0;

export interface BudgetItemRow {
  id: string;
  name: string;
  kind: Section;
  frequency?: Frequency;
  pay_month?: string | null;
  loan_account_id?: string | null;
  category_id: string;
  category_name: string;
  group: CategoryGroup;
  amount: number;
  actual_month_amount: number;
  remaining: number;
  progress_ratio: number | null;
  status: CategoryStatus;
  helper_text: string;
}

export const SECTIONS: Record<Section, { title: string; hint: string; placeholder: string; catType: "income" | "expense" | "transfer"; suggestions: string[] }> = {
  income: { title: "Ingresos", hint: "Lo que entra cada mes. Tu ingreso mensual es la suma de estas líneas.", placeholder: "Ej. Renta del depa", catType: "income",
    suggestions: ["Sueldo", "Honorarios", "Rentas", "Dividendos", "Intereses", "Pensión"] },
  expense: { title: "Gastos", hint: "Escribe cada gasto, asígnale su categoría, si es Esencial, de Comodidad o Deuda, y cuánto le presupuestas.", placeholder: "Ej. Luz", catType: "expense",
    suggestions: ["Renta", "Hipoteca", "Luz", "Agua", "Gas", "Internet", "Teléfono", "Súper", "Gasolina", "Netflix", "Gimnasio", "Colegiatura"] },
  savings: { title: "Ahorro e Inversión", hint: "Cuánto apartas cada mes para tu futuro. Es un traspaso, no un gasto.", placeholder: "Ej. Fondo de emergencia", catType: "transfer",
    suggestions: ["Fondo de emergencia", "Retiro", "Inversión", "Metas", "Seguro con ahorro (PPR, dotal, educativo)"] },
};

export const CATEGORY_TYPE_LABELS = { income: "Ingreso", expense: "Gasto", transfer: "Traspaso" } as const;

export const isDebtName = (n?: string | null) => (n ?? "").trim().toLowerCase() === "deudas y obligaciones";
export const DEBT_BLOCK = { title: "Deudas y Obligaciones", hint: "Lo que pagas por deudas: préstamos, meses sin intereses, intereses, comisiones y anualidades.", placeholder: "Ej. Préstamo personal" };

export const GROUP_LABELS_SHORT: Record<PlanGroup | "unassigned", string> = {
  essentials: "Esenciales",
  comfort: "Comodidad",
  debt: "Deudas",
  savings_investment: "Ahorro e Inversión",
  unassigned: "Sin destino",
};
